import {Async} from 'Core/models/Async';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {CaseNumber, UserAssignedCaseNumber, CaseNumberError, matchCaseNumber} from './CaseNumber';
import {NonEmptyString, StringUnion, EmptyString} from 'Core/models/EmptyString';
import {AgentList, EmptyAgentList, ValidAgentList} from './AgentList';
import {BillingAgent} from './BillingAgent';

export class UnvalidatedOrdinaryVerifyCriticalInformation {
  public readonly key = 'UnvalidatedOrdinaryVerifyCriticalInformation';

  constructor(
    public readonly caseNumber: CaseNumber,
    public readonly caseName: StringUnion,
    public readonly transit: StringUnion,
    public readonly accountNumber: StringUnion,
    public readonly controlNumber: StringUnion,
    public readonly applicant: StringUnion,
    public readonly agentList: AgentList
  ) {}
}

export class InvalidOrdinaryVerifyCriticalInformation {
  public readonly key = 'InvalidOrdinaryVerifyCriticalInformation';

  constructor(
    public readonly caseNumber: CaseNumber,
    public readonly caseName: StringUnion,
    public readonly transit: StringUnion,
    public readonly accountNumber: StringUnion,
    public readonly controlNumber: StringUnion,
    public readonly applicant: StringUnion,
    public readonly agentList: AgentList
  ) {}
}

type OrdinaryVerifyCriticalInformationProps = {
  caseNumber: CaseNumber;
  caseName: StringUnion;
  applicant: StringUnion;
  controlNumber: StringUnion;
  transit: StringUnion;
  accountNumber: StringUnion;
  agentList: AgentList;
};

export class ValidOrdinaryVerifyCriticalInformation {
  public static createFromApp({
    caseNumber,
    caseName,
    transit,
    accountNumber,
    controlNumber,
    applicant,
    agentList,
  }: OrdinaryVerifyCriticalInformationProps) {
    return this.create(caseNumber, caseName, transit, accountNumber, controlNumber, applicant, agentList);
  }

  public static create(
    caseNumber: CaseNumber,
    caseName: StringUnion,
    transit: StringUnion,
    accountNumber: StringUnion,
    controlNumber: StringUnion,
    applicant: StringUnion,
    agentList: AgentList
  ) {
    return new ValidOrdinaryVerifyCriticalInformation(
      caseNumber,
      caseName,
      transit,
      accountNumber,
      controlNumber,
      applicant,
      agentList
    );
  }

  public readonly key = 'ValidOrdinaryVerifyCriticalInformation';
  private constructor(
    public readonly caseNumber: CaseNumber,
    public readonly caseName: StringUnion,
    public readonly transit: StringUnion,
    public readonly accountNumber: StringUnion,
    public readonly controlNumber: StringUnion,
    public readonly applicant: StringUnion,
    public readonly agentList: AgentList
  ) {}
}

export type OrdinaryVerifyCriticalInformation =
  | UnvalidatedOrdinaryVerifyCriticalInformation
  | InvalidOrdinaryVerifyCriticalInformation
  | ValidOrdinaryVerifyCriticalInformation;

export type OrdinaryVerifyCriticalInformationOptions<TReturn> = ToMatchOptions<
  OrdinaryVerifyCriticalInformation,
  'key',
  TReturn
>;

export const matchOrdinaryVerifyCriticalInformation = <TReturn>(
  options: OrdinaryVerifyCriticalInformationOptions<TReturn>
) => (app: OrdinaryVerifyCriticalInformation): TReturn => {
  switch (app.key) {
    case 'InvalidOrdinaryVerifyCriticalInformation':
      return options.InvalidOrdinaryVerifyCriticalInformation(app);
    case 'UnvalidatedOrdinaryVerifyCriticalInformation':
      return options.UnvalidatedOrdinaryVerifyCriticalInformation(app);
    case 'ValidOrdinaryVerifyCriticalInformation':
      return options.ValidOrdinaryVerifyCriticalInformation(app);
  }
};

export const changeCaseNumber = (caseNumber: Async<string>) => (app: OrdinaryVerifyCriticalInformation) =>
  ValidOrdinaryVerifyCriticalInformation.createFromApp({
    ...app,
    caseNumber,
  });

export const changeUserCaseNumber = (caseNumber: string) => (app: OrdinaryVerifyCriticalInformation) => {
  return ValidOrdinaryVerifyCriticalInformation.createFromApp({
    ...app,
    caseNumber: UserAssignedCaseNumber.create(caseNumber),
  });
};

export const changeCaseNumberError = (caseNumber: string) => (app: OrdinaryVerifyCriticalInformation) =>
  ValidOrdinaryVerifyCriticalInformation.createFromApp({
    ...app,
    caseNumber: CaseNumberError.create(caseNumber),
  });

export const changeCaseName = (caseName: string) => (app: OrdinaryVerifyCriticalInformation) =>
  ValidOrdinaryVerifyCriticalInformation.createFromApp({
    ...app,
    caseName: NonEmptyString.create(caseName),
  });

export const changeTransit = (transit: string) => (app: OrdinaryVerifyCriticalInformation) =>
  ValidOrdinaryVerifyCriticalInformation.createFromApp({
    ...app,
    transit: NonEmptyString.create(transit),
  });

export const changeAccountNumber = (accountNumber: string) => (app: OrdinaryVerifyCriticalInformation) =>
  ValidOrdinaryVerifyCriticalInformation.createFromApp({
    ...app,
    accountNumber: NonEmptyString.create(accountNumber),
  });

export const changeApplicant = (applicant: string) => (app: OrdinaryVerifyCriticalInformation) =>
  ValidOrdinaryVerifyCriticalInformation.createFromApp({
    ...app,
    applicant: NonEmptyString.create(applicant),
  });

export const changeControlNumber = (controlNumber: string) => (app: OrdinaryVerifyCriticalInformation) =>
  ValidOrdinaryVerifyCriticalInformation.createFromApp({
    ...app,
    controlNumber: NonEmptyString.create(controlNumber),
  });

export const changeAgentList = (agentList: AgentList) => (app: OrdinaryVerifyCriticalInformation) =>
  ValidOrdinaryVerifyCriticalInformation.createFromApp({
    ...app,
    agentList: ValidAgentList.create(agentList.agentList),
  });

export const EmptyOrdinaryVerifyCriticalInformation = new UnvalidatedOrdinaryVerifyCriticalInformation(
  new UserAssignedCaseNumber(''),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyAgentList
);

export const isVerified = (
  originalOrdinaryVerifyCriticalInformation: OrdinaryVerifyCriticalInformation,
  ordinaryVerifyCriticalInformation: OrdinaryVerifyCriticalInformation,
  billingAgent: BillingAgent
): boolean => {
  const getCaseNumber = (caseNumber: CaseNumber) =>
    matchCaseNumber({
      Loading: () => '',
      Loaded: caseNumber => caseNumber,
      Unassigned: () => '',
      UserAssignedCaseNumber: caseNumber => caseNumber,
      Error: () => '',
      CaseNumberError: () => '',
    })(caseNumber)!;

  const {
    agentList,
    caseNumber,
    controlNumber,
    accountNumber,
    transit,
    applicant,
  } = originalOrdinaryVerifyCriticalInformation;
  const {
    agentList: newAgentList,
    applicant: newApplicant,
    caseNumber: newCaseNumber,
    controlNumber: newControlNumber,
    accountNumber: newAccountNumber,
    transit: newTransit,
  } = ordinaryVerifyCriticalInformation;

  return (
    (agentList.agentList.size > 0 &&
      agentList.agentList.count(agentInformation => newAgentList.agentList.toArray().includes(agentInformation)) ===
        agentList.agentList.size &&
      applicant.value !== '' &&
      applicant.value.toLowerCase() === newApplicant.value.toLowerCase() &&
      ((getCaseNumber(caseNumber) !== '' &&
        controlNumber.value !== '' &&
        getCaseNumber(caseNumber).toLowerCase() === getCaseNumber(newCaseNumber).toLowerCase() &&
        controlNumber.value === newControlNumber.value) ||
        (accountNumber.value !== '' &&
          transit.value !== '' &&
          accountNumber.value === newAccountNumber.value &&
          transit.value === newTransit.value)) &&
      applicant.value !== '' &&
      applicant.value.toLowerCase() === newApplicant.value.toLowerCase()) ||
    (!billingAgent.pac &&
      agentList.agentList.size > 0 &&
      agentList.agentList.count(agentInformation => newAgentList.agentList.toArray().includes(agentInformation)) ===
        agentList.agentList.size &&
      applicant.value !== '' &&
      applicant.value.toLowerCase() === newApplicant.value.toLowerCase()) ||
    (billingAgent.pac &&
      agentList.agentList.size > 0 &&
      agentList.agentList.count(agentInformation => newAgentList.agentList.toArray().includes(agentInformation)) ===
        agentList.agentList.size &&
      applicant.value !== '' &&
      applicant.value.toLowerCase() === newApplicant.value.toLowerCase() &&
      accountNumber.value !== '' &&
      accountNumber.value.toLowerCase() === newAccountNumber.value.toLowerCase() &&
      transit.value !== '' &&
      transit.value.toLowerCase() === newTransit.value.toLowerCase())
  );
};

export const isValidOrdinaryVerifyCriticalInformation = (
  ordinaryVerifyCriticalInformation: OrdinaryVerifyCriticalInformation
): ordinaryVerifyCriticalInformation is ValidOrdinaryVerifyCriticalInformation => {
  return ordinaryVerifyCriticalInformation.key === 'ValidOrdinaryVerifyCriticalInformation';
};
